import React, { useState, useContext, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './KnowledgeBase.css';
import { UserContext } from '../../contexts/CurrentUserContext';
import {
  getLessons,
  mapApiLessonToOrganizationalCourse,
  mapApiLessonToCourse,
  type ApiLesson,
} from '../../api/knowledgeApi';
import type { Course } from './Training/types';
// @ts-ignore
import logo from '../../images/logo.svg';
// @ts-ignore — используется в закомментированном блоке «База знаний»
// import openBookIcon from '../../images/KnowledgeBase/OpenBook.svg';
// @ts-ignore
import graduateIcon from '../../images/KnowledgeBase/Graduate.svg';
// @ts-ignore
import booksIllustration from '../../images/KnowledgeBase/Books.svg';
// @ts-ignore
import webinarIllustration from '../../images/KnowledgeBase/Webinar.svg';

interface Category {
  id: string;
  name: string;
  description: string;
  icon: string;
  iconBackground: string;
  subcategories: Subcategory[];
}

interface Subcategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  iconBackground: string;
}

const KnowledgeBase: React.FC = () => {
  const navigate = useNavigate();
  const { userData } = useContext(UserContext);
  const categoriesSectionRef = useRef<HTMLElement>(null);
  const [isOpen, setIsOpen] = useState<{ [key: string]: boolean }>({
    knowledge: false,
    training: false
  });
  const [coursesProgress, setCoursesProgress] = useState<ApiLesson[]>([]);
  const [progressLoading, setProgressLoading] = useState(true);
  const [progressError, setProgressError] = useState<string | null>(null);

  // Загрузка прогресса курсов через тот же getLessons, что и страница «Организационное обучение»
  useEffect(() => {
    const fetchProgress = async () => {
      setProgressLoading(true);
      setProgressError(null);
      const allCourses: ApiLesson[] = [];
      try {
        const [orgResponse, profileResponse] = await Promise.allSettled([
          getLessons({ type: 'organizational', page: 1, page_size: 100 }),
          getLessons({ type: 'profile', page: 1, page_size: 100 }),
        ]);
        if (orgResponse.status === 'fulfilled') {
          allCourses.push(...(orgResponse.value.results || []));
        } else {
          console.error('Ошибка загрузки организационных курсов:', orgResponse.reason);
        }
        if (profileResponse.status === 'fulfilled') {
          allCourses.push(...(profileResponse.value.results || []));
        } else {
          console.error('Ошибка загрузки профильных курсов:', profileResponse.reason);
        }
        if (allCourses.length === 0 && (orgResponse.status === 'rejected' || profileResponse.status === 'rejected')) {
          setProgressError('Не удалось загрузить данные о прогрессе');
        }
        setCoursesProgress(allCourses);
      } catch (err) {
        console.error('Ошибка загрузки прогресса курсов:', err);
        setProgressError('Не удалось загрузить данные о прогрессе');
        setCoursesProgress([]);
      } finally {
        setProgressLoading(false);
      }
    };

    fetchProgress();
  }, []);

  // Вычисляем прогресс для каждого курса (как на странице организационного обучения)
  const getCourseProgress = (apiLesson: ApiLesson) => {
    const total = apiLesson.lessons?.length || 0;
    const completed = apiLesson.lessons?.filter((l) => l.is_completed).length || 0;
    const percentage = apiLesson.completed_lessons_percentage ?? (total > 0 ? (completed / total) * 100 : 0);
    return { total, completed, percentage, isFullyCompleted: percentage >= 100 };
  };

  // В прогрессе — только начатые (есть прогресс > 0), но не 100%. Завершённые — только в своём блоке.
  const inProgressCourses = coursesProgress.filter((item) => {
    const { percentage, isFullyCompleted } = getCourseProgress(item);
    const hasLessons = (item.lessons?.length || 0) > 0;
    return hasLessons && !isFullyCompleted && percentage > 0;
  });
  const completedCourses = coursesProgress.filter((item) => getCourseProgress(item).isFullyCompleted);

  const handleCourseClick = (apiLesson: ApiLesson) => {
    const isOrganizational = apiLesson.type === 'organizational';
    const course: Course = isOrganizational
      ? mapApiLessonToOrganizationalCourse(apiLesson, 'all')
      : mapApiLessonToCourse(apiLesson);

    if (course.lessons.length > 0) {
      const firstLessonId = course.lessons[0].id;
      if (isOrganizational) {
        navigate(`/knowledge-base/organizational-training/${course.id}/lesson/${firstLessonId}`);
      } else {
        const categoryId = course.tags?.[0] || 'all';
        navigate(`/knowledge-base/profile-training/${categoryId}/${course.id}/lesson/${firstLessonId}`);
      }
    }
  };

  // Проверка прав доступа администратора базы знаний
  const hasKnowledgeBaseAdminAccess =
    userData &&
    userData.access_levels &&
    userData.access_levels.some(
      (level: { name: string }) => level.name === 'АДМС' || level.name === 'АДМП'
    );

  const toggleSection = (section: string): void => {
    setIsOpen({
      ...isOpen,
      [section]: !isOpen[section]
    });
  };

  const handleGoToCatalog = (): void => {
    // Открываем раздел "Обучение"
    setIsOpen({
      ...isOpen,
      training: true
    });
    
    // Прокручиваем к секции категорий
    setTimeout(() => {
      if (categoriesSectionRef.current) {
        categoriesSectionRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }, 100);
  };

  const handleSubcategoryClick = (subcategoryId: string): void => {
    if (subcategoryId === 'profile-training') {
      navigate('/knowledge-base/profile-training');
      return;
    }
    if (subcategoryId === 'organizational-training') {
      navigate('/knowledge-base/organizational-training');
      return;
    }
    // TODO: Add navigation for other subcategories
  };

  const categories: Category[] = [
    // Блок «База знаний» временно отключён — бэкенд не готов
    // {
    //   id: 'knowledge',
    //   name: 'База знаний',
    //   description: 'Обучение. Стандарты. Решения. Всё в одном месте.',
    //   icon: openBookIcon,
    //   iconBackground: 'transparent',
    //   subcategories: [
    //     {
    //       id: 'standards',
    //       name: 'Стандарты',
    //       description: 'Внутренние стандарты и регламенты компании',
    //       icon: '📋',
    //       iconBackground: '#e3f2fd'
    //     },
    //     {
    //       id: 'regulations',
    //       name: 'Нормативка',
    //       description: 'Нормативные документы',
    //       icon: '📜',
    //       iconBackground: '#e3f2fd'
    //     },
    //     {
    //       id: 'lessons',
    //       name: 'Извлеченные уроки',
    //       description: 'База решений и кейсы',
    //       icon: '💡',
    //       iconBackground: '#e3f2fd'
    //     }
    //   ]
    // },
    {
      id: 'training',
      name: 'Обучение',
      description: 'Курсы и материалы для профессионального развития.',
      icon: graduateIcon,
      iconBackground: 'transparent',
      subcategories: [
        {
          id: 'profile-training',
          name: 'Профильное обучение',
          description: 'Профессиональные и технические навыки',
          icon: '🎯',
          iconBackground: '#e8f5e8'
        },
        {
          id: 'organizational-training',
          name: 'Организационное обучение',
          description: 'Корпоративные навыки и процессы',
          icon: '🏢',
          iconBackground: '#e8f5e8'
        }
      ]
    }
  ];

  return (
    <div className="knowledge-base">
      {/* Admin Button */}
      {hasKnowledgeBaseAdminAccess && (
        <button
          className="knowledge-base__admin-button"
          onClick={() => navigate('/knowledge-base/admin')}
          title="Администрирование базы знаний"
        >
          Администрирование
        </button>
      )}
      
      {/* Hero Section */}
      <section className="knowledge-base__hero">
        <div className="knowledge-base__hero-content">
          <div className="knowledge-base__logo-container">
            <img src={logo} alt="AQMOL project" className="knowledge-base__logo" />
          </div>
          <div className="knowledge-base__title-section">
            <h1 className="knowledge-base__title">База знаний</h1>
          </div>
        </div>
            <p className="knowledge-base__tagline">
              Обучение. Стандарты. Решения. Всё в одном месте.
            </p>
      </section>

      {/* Categories Section */}
      <section ref={categoriesSectionRef} className="knowledge-base__categories">
        <div className="knowledge-base__categories-container">
          {categories.map((category) => (
            <div
              key={category.id}
              className={`knowledge-base__category-card ${
                isOpen[category.id] ? 'knowledge-base__category-card--open' : ''
              }`}
            >
              <div 
                className="knowledge-base__category-header"
                onClick={() => toggleSection(category.id)}
                style={{ backgroundColor: category.iconBackground }}
              >
                <div className="knowledge-base__category-icon">
                  <img src={category.icon} alt={category.name} />
                </div>
                <div className="knowledge-base__category-content">
                  <h3 className="knowledge-base__category-name">{category.name}</h3>
                </div>
                <button
                  className={`knowledge-base__dropdown-button ${
                    isOpen[category.id] ? 'knowledge-base__dropdown-button--open' : ''
                  }`}
                >
                  ↑
                </button>
              </div>
              
              {isOpen[category.id] && (
                <div className="knowledge-base__subcategories">
                  {category.subcategories.map((subcategory) => (
                    <div
                      key={subcategory.id}
                      className="knowledge-base__subcategory-card"
                      onClick={() => handleSubcategoryClick(subcategory.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div 
                        className="knowledge-base__subcategory-icon"
                        style={{ backgroundColor: subcategory.iconBackground }}
                      >
                        <span className="knowledge-base__subcategory-emoji">
                          {subcategory.icon}
                        </span>
                      </div>
                      <div className="knowledge-base__subcategory-content">
                        <h4 className="knowledge-base__subcategory-name">
                          {subcategory.name}
                        </h4>
                        <p className="knowledge-base__subcategory-description">
                          {subcategory.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Progress Section */}
      <section className="knowledge-base__progress">
        <h2 className="knowledge-base__section-title">Ваш прогресс обучения</h2>
        {progressLoading ? (
          <div className="knowledge-base__empty-state">
            <p className="knowledge-base__loading-text">Загрузка...</p>
          </div>
        ) : progressError ? (
          <div className="knowledge-base__empty-state">
            <p className="knowledge-base__error-text">{progressError}</p>
          </div>
        ) : inProgressCourses.length > 0 ? (
          <div className="knowledge-base__courses-grid">
            {inProgressCourses.map((item) => {
              const progress = getCourseProgress(item);
              return (
                <button
                  key={item.id}
                  type="button"
                  className="knowledge-base__course-card"
                  onClick={() => handleCourseClick(item)}
                >
                  <div className="knowledge-base__course-card-image">
                    {item.images?.[0]?.image ? (
                      <img src={item.images[0].image} alt={item.name} />
                    ) : (
                      <span>📘</span>
                    )}
                  </div>
                  <div className="knowledge-base__course-card-content">
                    <h4 className="knowledge-base__course-card-title">{item.name}</h4>
                    <p className="knowledge-base__course-card-progress">
                      {progress.completed} из {progress.total} уроков · {Math.round(progress.percentage)}%
                    </p>
                    <div className="knowledge-base__course-card-progress-bar">
                      <div
                        className="knowledge-base__course-card-progress-fill"
                        style={{ width: `${progress.percentage}%` }}
                      />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="knowledge-base__empty-state">
            <div className="knowledge-base__empty-state-content">
              <div className="knowledge-base__empty-state-text">
                <p>
                  У вас пока нет активных курсов. Выберите курс из каталога и начните обучение
                </p>
                <button 
                  className="knowledge-base__catalog-button"
                  onClick={handleGoToCatalog}
                >
                  Перейти в каталог
                </button>
              </div>
              <div className="knowledge-base__empty-state-illustration">
                <img src={booksIllustration} alt="Обучение" />
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Completed Courses Section */}
      <section className="knowledge-base__completed">
        <h2 className="knowledge-base__section-title">Завершенные курсы</h2>
        {progressLoading ? (
          <div className="knowledge-base__empty-state">
            <p className="knowledge-base__loading-text">Загрузка...</p>
          </div>
        ) : completedCourses.length > 0 ? (
          <div className="knowledge-base__courses-grid">
            {completedCourses.map((item) => (
              <button
                key={item.id}
                type="button"
                className="knowledge-base__course-card knowledge-base__course-card--completed"
                onClick={() => handleCourseClick(item)}
              >
                <div className="knowledge-base__course-card-image">
                  {item.images?.[0]?.image ? (
                    <img src={item.images[0].image} alt={item.name} />
                  ) : (
                    <span>📘</span>
                  )}
                </div>
                <div className="knowledge-base__course-card-content">
                  <h4 className="knowledge-base__course-card-title">{item.name}</h4>
                  <p className="knowledge-base__course-card-progress knowledge-base__course-card-progress--done">
                    ✓ Завершён
                  </p>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="knowledge-base__empty-state">
            <div className="knowledge-base__empty-state-content">
              <div className="knowledge-base__empty-state-text">
                <p>
                  Вы ещё не завершили ни один курс. Когда вы пройдёте курс — он появится здесь.
                </p>
                <button 
                  className="knowledge-base__catalog-button"
                  onClick={handleGoToCatalog}
                >
                  Перейти в каталог
                </button>
              </div>
              <div className="knowledge-base__empty-state-illustration">
                <img src={webinarIllustration} alt="Завершенные курсы" />
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default KnowledgeBase;
